import React, { useState } from 'react';
import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CheckIcon from '@mui/icons-material/Check';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'context/AuthContext';
import { useCartContext } from 'context/CartContext';
import { useStorefrontContext } from 'context/StorefrontContext';

interface CartSwitcherProps {
  contrast?: boolean;
}

const CartSwitcher: React.FC<CartSwitcherProps> = ({ contrast }) => {
  const { t } = useTranslation('common');
  const { isAuthenticated } = useAuthContext();
  const { features } = useStorefrontContext();
  const { cart, carts, createCart, activateCart } = useCartContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (!isAuthenticated || !features.multipleCartsEnabled) return null;

  const active = carts.find((item) => item.isActive) || carts[0];
  const label = cart?.name || active?.name || t('nav.carts');

  const handleCreate = async (kind: 'order' | 'quote') => {
    setAnchorEl(null);
    await createCart(kind);
  };

  return (
    <>
      <Tooltip title={label}>
        <IconButton
          color={contrast ? 'inherit' : 'default'}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={t('nav.switchCart')}
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
        >
          <AssignmentOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {carts.map((item) => (
          <MenuItem
            key={item.cartId}
            selected={item.isActive}
            onClick={async () => {
              setAnchorEl(null);
              if (!item.isActive) await activateCart(item.cartId);
            }}
          >
            {item.isActive ? (
              <ListItemIcon>
                <CheckIcon fontSize="small" />
              </ListItemIcon>
            ) : (
              <ListItemIcon />
            )}
            <ListItemText
              primary={item.name}
              secondary={
                item.kind === 'quote'
                  ? t('nav.cartKindQuote')
                  : t('nav.cartKindOrder')
              }
            />
          </MenuItem>
        ))}
        {carts.length > 0 && <Divider />}
        <MenuItem onClick={() => handleCreate('order')}>
          <ListItemIcon>
            <AddOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('nav.newCart')} />
        </MenuItem>
        <MenuItem onClick={() => handleCreate('quote')}>
          <ListItemIcon>
            <RequestQuoteOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('nav.newQuoteCart')} />
        </MenuItem>
      </Menu>
    </>
  );
};

export default CartSwitcher;
